import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';

interface TrainingCard {
  id: number;
  section_id: number;
  title: string;
  description: string;
  embed_url: string;
  thumbnail_url: string;
  sort_order: number;
}

interface TrainingSection {
  id: number;
  title: string;
  sort_order: number;
  cards: TrainingCard[];
}

const TrainingAdmin: React.FC = () => {
  const { token } = useAuth();
  const [sections, setSections] = useState<TrainingSection[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Section editing
  const [editingSectionId, setEditingSectionId] = useState<number | null>(null);
  const [editingSectionTitle, setEditingSectionTitle] = useState('');
  const [newSectionTitle, setNewSectionTitle] = useState('');

  // Card editing modal
  const [cardModal, setCardModal] = useState<{ mode: 'add' | 'edit'; sectionId: number; card?: TrainingCard } | null>(null);
  const [cardForm, setCardForm] = useState({ title: '', description: '', embed_url: '', thumbnail_url: '' });
  const [uploadingThumb, setUploadingThumb] = useState(false);

  useEffect(() => {
    fetchData();
  }, [token]);

  const fetchData = async () => {
    try {
      const response = await fetch('/api/training', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        setSections(await response.json());
      }
    } catch (error) {
      console.error('Error fetching training data:', error);
    } finally {
      setLoading(false);
    }
  };

  // ─── Section CRUD ─────────────────────────────────────────────────────────

  const addSection = async () => {
    if (!newSectionTitle.trim()) return;
    setSaving(true);
    try {
      const response = await fetch('/api/training/sections', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: newSectionTitle.trim() })
      });
      if (response.ok) {
        setNewSectionTitle('');
        fetchData();
      }
    } finally {
      setSaving(false);
    }
  };

  const updateSection = async (id: number) => {
    if (!editingSectionTitle.trim()) return;
    setSaving(true);
    try {
      await fetch(`/api/training/sections/${id}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: editingSectionTitle.trim() })
      });
      setEditingSectionId(null);
      fetchData();
    } finally {
      setSaving(false);
    }
  };

  const deleteSection = async (id: number, title: string) => {
    if (!window.confirm(`Delete section "${title}" and all its cards? This cannot be undone.`)) return;
    try {
      await fetch(`/api/training/sections/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchData();
    } catch (error) {
      console.error('Error deleting section:', error);
    }
  };

  const moveSectionUp = async (index: number) => {
    if (index === 0) return;
    const newOrder = sections.map(s => s.id);
    [newOrder[index - 1], newOrder[index]] = [newOrder[index], newOrder[index - 1]];
    await fetch('/api/training/sections/reorder', {
      method: 'PUT',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ order: newOrder })
    });
    fetchData();
  };

  const moveSectionDown = async (index: number) => {
    if (index === sections.length - 1) return;
    const newOrder = sections.map(s => s.id);
    [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
    await fetch('/api/training/sections/reorder', {
      method: 'PUT',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ order: newOrder })
    });
    fetchData();
  };

  // ─── Card CRUD ────────────────────────────────────────────────────────────

  const openAddCard = (sectionId: number) => {
    setCardForm({ title: '', description: '', embed_url: '', thumbnail_url: '' });
    setCardModal({ mode: 'add', sectionId });
  };

  const openEditCard = (sectionId: number, card: TrainingCard) => {
    setCardForm({ title: card.title, description: card.description, embed_url: card.embed_url, thumbnail_url: card.thumbnail_url || '' });
    setCardModal({ mode: 'edit', sectionId, card });
  };

  const uploadThumbnail = async (file: File) => {
    setUploadingThumb(true);
    try {
      const formData = new FormData();
      formData.append('thumbnail', file);
      const response = await fetch('/api/training/upload-thumbnail', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      if (response.ok) {
        const data = await response.json();
        setCardForm(prev => ({ ...prev, thumbnail_url: data.url }));
      }
    } catch (error) {
      console.error('Error uploading thumbnail:', error);
    } finally {
      setUploadingThumb(false);
    }
  };

  const removeThumbnail = async () => {
    if (cardForm.thumbnail_url) {
      // Clean up the file on server
      try {
        await fetch('/api/training/thumbnail', {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ url: cardForm.thumbnail_url })
        });
      } catch (error) {
        console.error('Error deleting thumbnail file:', error);
      }
      setCardForm(prev => ({ ...prev, thumbnail_url: '' }));
    }
  };

  const saveCard = async () => {
    if (!cardForm.title.trim() || !cardModal) return;
    setSaving(true);
    try {
      if (cardModal.mode === 'add') {
        await fetch('/api/training/cards', {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ section_id: cardModal.sectionId, ...cardForm })
        });
      } else if (cardModal.card) {
        await fetch(`/api/training/cards/${cardModal.card.id}`, {
          method: 'PATCH',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify(cardForm)
        });
      }
      setCardModal(null);
      fetchData();
    } finally {
      setSaving(false);
    }
  };

  const deleteCard = async (cardId: number, title: string) => {
    if (!window.confirm(`Delete card "${title}"? This cannot be undone.`)) return;
    try {
      await fetch(`/api/training/cards/${cardId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchData();
    } catch (error) {
      console.error('Error deleting card:', error);
    }
  };

  const moveCardUp = async (section: TrainingSection, cardIndex: number) => {
    if (cardIndex === 0) return;
    const newOrder = section.cards.map(c => c.id);
    [newOrder[cardIndex - 1], newOrder[cardIndex]] = [newOrder[cardIndex], newOrder[cardIndex - 1]];
    await fetch('/api/training/cards/reorder', {
      method: 'PUT',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ order: newOrder })
    });
    fetchData();
  };

  const moveCardDown = async (section: TrainingSection, cardIndex: number) => {
    if (cardIndex === section.cards.length - 1) return;
    const newOrder = section.cards.map(c => c.id);
    [newOrder[cardIndex], newOrder[cardIndex + 1]] = [newOrder[cardIndex + 1], newOrder[cardIndex]];
    await fetch('/api/training/cards/reorder', {
      method: 'PUT',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ order: newOrder })
    });
    fetchData();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Training Portal Content</h3>
          <p className="text-sm text-gray-500 mt-1">
            Manage sections and video cards that all users see in the Training portal.
          </p>
        </div>
        <a
          href="/training"
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
        >
          Preview portal
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
          </svg>
        </a>
      </div>

      {/* Add New Section */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">Add New Section</label>
        <div className="flex gap-2">
          <input
            type="text"
            value={newSectionTitle}
            onChange={e => setNewSectionTitle(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && addSection()}
            placeholder="e.g. Getting Started"
            className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <button
            onClick={addSection}
            disabled={saving || !newSectionTitle.trim()}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            Add Section
          </button>
        </div>
      </div>

      {/* Sections List */}
      {sections.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
          <svg className="w-12 h-12 mx-auto text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
          </svg>
          <p className="text-gray-500">No sections yet. Add your first section above.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {sections.map((section, sectionIndex) => (
            <div key={section.id} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              {/* Section Header */}
              <div className="flex items-center gap-3 px-4 py-3 bg-gray-50 border-b border-gray-200">
                {/* Reorder buttons */}
                <div className="flex flex-col gap-0.5">
                  <button
                    onClick={() => moveSectionUp(sectionIndex)}
                    disabled={sectionIndex === 0}
                    className="p-0.5 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                    title="Move up"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                    </svg>
                  </button>
                  <button
                    onClick={() => moveSectionDown(sectionIndex)}
                    disabled={sectionIndex === sections.length - 1}
                    className="p-0.5 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                    title="Move down"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                </div>

                {/* Section title (inline editing) */}
                {editingSectionId === section.id ? (
                  <div className="flex-1 flex items-center gap-2">
                    <input
                      type="text"
                      value={editingSectionTitle}
                      onChange={e => setEditingSectionTitle(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') updateSection(section.id);
                        if (e.key === 'Escape') setEditingSectionId(null);
                      }}
                      className="flex-1 rounded border border-gray-300 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      autoFocus
                    />
                    <button
                      onClick={() => updateSection(section.id)}
                      className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                    >
                      Save
                    </button>
                    <button
                      onClick={() => setEditingSectionId(null)}
                      className="text-sm text-gray-500 hover:text-gray-700"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <div className="flex-1 flex items-center gap-3">
                    <h4 className="font-semibold text-gray-900">{section.title}</h4>
                    <span className="text-xs text-gray-400">{section.cards.length} card{section.cards.length !== 1 ? 's' : ''}</span>
                  </div>
                )}

                {/* Section actions */}
                {editingSectionId !== section.id && (
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => { setEditingSectionId(section.id); setEditingSectionTitle(section.title); }}
                      className="p-1.5 text-gray-400 hover:text-blue-600 rounded-md hover:bg-blue-50 transition-colors"
                      title="Edit section title"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                      </svg>
                    </button>
                    <button
                      onClick={() => deleteSection(section.id, section.title)}
                      className="p-1.5 text-gray-400 hover:text-red-600 rounded-md hover:bg-red-50 transition-colors"
                      title="Delete section"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                )}
              </div>

              {/* Cards list */}
              <div className="p-4">
                {section.cards.length === 0 ? (
                  <p className="text-sm text-gray-400 text-center py-4">No cards in this section.</p>
                ) : (
                  <div className="space-y-2 mb-4">
                    {section.cards.map((card, cardIndex) => (
                      <div key={card.id} className="flex items-center gap-3 px-3 py-2.5 bg-gray-50 rounded-lg border border-gray-100">
                        {/* Card reorder */}
                        <div className="flex flex-col gap-0.5">
                          <button
                            onClick={() => moveCardUp(section, cardIndex)}
                            disabled={cardIndex === 0}
                            className="p-0.5 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                            </svg>
                          </button>
                          <button
                            onClick={() => moveCardDown(section, cardIndex)}
                            disabled={cardIndex === section.cards.length - 1}
                            className="p-0.5 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </button>
                        </div>

                        {/* Card info */}
                        {card.thumbnail_url && (
                          <img src={card.thumbnail_url} alt="" className="h-10 w-16 rounded object-cover flex-shrink-0" />
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm text-gray-900 truncate">{card.title}</p>
                          <div className="flex items-center gap-2">
                            {card.embed_url && (
                              <span className="text-xs text-gray-400">Embed code added</span>
                            )}
                            {card.thumbnail_url && (
                              <span className="text-xs text-green-500">Thumbnail</span>
                            )}
                          </div>
                        </div>

                        {/* Card actions */}
                        <button
                          onClick={() => openEditCard(section.id, card)}
                          className="p-1.5 text-gray-400 hover:text-blue-600 rounded-md hover:bg-blue-50 transition-colors"
                          title="Edit card"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => deleteCard(card.id, card.title)}
                          className="p-1.5 text-gray-400 hover:text-red-600 rounded-md hover:bg-red-50 transition-colors"
                          title="Delete card"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <button
                  onClick={() => openAddCard(section.id)}
                  className="w-full py-2 border-2 border-dashed border-gray-200 rounded-lg text-sm text-gray-500 hover:text-blue-600 hover:border-blue-300 transition-colors flex items-center justify-center gap-1.5"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Add Card
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Card Add/Edit Modal */}
      {cardModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setCardModal(null)}>
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full" onClick={e => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                {cardModal.mode === 'add' ? 'Add Training Card' : 'Edit Training Card'}
              </h3>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                <input
                  type="text"
                  value={cardForm.title}
                  onChange={e => setCardForm(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="e.g. How to create a project"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={cardForm.description}
                  onChange={e => setCardForm(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Brief description of what this walkthrough covers..."
                  rows={3}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Storylane Embed Code</label>
                <textarea
                  value={cardForm.embed_url}
                  onChange={e => setCardForm(prev => ({ ...prev, embed_url: e.target.value }))}
                  placeholder='<div><script async src="https://js.storylane.io/js/v2/storylane.js"></script>...'
                  rows={4}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                />
                <p className="text-xs text-gray-400 mt-1">Paste the full embed code from Storylane</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Thumbnail Image</label>
                {cardForm.thumbnail_url ? (
                  <div className="flex items-center gap-3">
                    <img
                      src={cardForm.thumbnail_url}
                      alt="Thumbnail preview"
                      className="h-20 rounded-lg border border-gray-200 object-cover"
                    />
                    <div className="flex flex-col gap-1">
                      <label className="px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors cursor-pointer">
                        Replace
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={e => {
                            const file = e.target.files?.[0];
                            if (file) uploadThumbnail(file);
                          }}
                        />
                      </label>
                      <button
                        type="button"
                        onClick={removeThumbnail}
                        className="px-3 py-1.5 text-xs font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition-colors"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ) : (
                  <label className={`flex flex-col items-center justify-center w-full h-28 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${uploadingThumb ? 'border-blue-300 bg-blue-50' : 'border-gray-300 hover:border-blue-400 hover:bg-gray-50'}`}>
                    {uploadingThumb ? (
                      <div className="flex items-center gap-2 text-sm text-blue-600">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600" />
                        Uploading...
                      </div>
                    ) : (
                      <>
                        <svg className="w-8 h-8 text-gray-400 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <span className="text-xs text-gray-500">Click to upload thumbnail</span>
                        <span className="text-xs text-gray-400">JPG, PNG, GIF, WebP (max 5MB)</span>
                      </>
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={e => {
                        const file = e.target.files?.[0];
                        if (file) uploadThumbnail(file);
                      }}
                    />
                  </label>
                )}
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => setCardModal(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={saveCard}
                disabled={saving || !cardForm.title.trim()}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
              >
                {saving ? 'Saving...' : cardModal.mode === 'add' ? 'Add Card' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrainingAdmin;
